import BackIcon from "../../assets/svgs/scenes/chevron_dark.svg";

import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
    getBuildingDetails,
    getLocationPins,
    getMapDetails,
    getVenueMapsDetails,
    searchDirectionBetweenPins,
    initializeInteractiveMaps,
    updateInteractiveAnalytics
} from "../../features/interactivemap/interactivemapSlice";
import InteractiveExperience from "./InteractiveExperience";
import { isEqual, size } from "lodash";
import Chatbot from 'react-chatbot-kit'
import 'react-chatbot-kit/build/main.css'
import { createChatBotMessage, } from 'react-chatbot-kit';


import MessageParser from "./chatBotUtils/MessageParser.js";
import ActionProvider from "./chatBotUtils/ActionProvider.js";
import AmenityOptions from "./chatBotUtils/AmenityOptions.js";
import { Floatingbar as FloatingbarDesktop } from "./ui/floatingbar/floatingbarDesktop/Floatingbar.js";
import { Floatingbar as FloatingbarMobile } from "./ui/floatingbar/floatingbarMobile/Floatingbar.js";
import { Sidebar as SidebarDesktop } from "./ui/sidebar/sidebarDesktop/Sidebar.js";
import { Sidebar as SidebarMobile } from "./ui/sidebar/sidebarMobile/Sidebar.js";

import { ActionButtons } from "./ui/buttons/Buttons.js";
import ARModal from "./interactivecommon/ARModal.js";
import { detectMobile } from "../_utils/detectBrowser.js";

export default function InteractiveMapsHome() {
    const params = useParams();
    const dispatch = useDispatch();
    const mapCode = params.mapCode;

    let isInteractiveMapsInit = true;
    sessionStorage.setItem("InteractiveMapsInit", true);

    const [fetching, setFetching] = useState(true);
    const [mapDetails, setMapDetails] = useState(null);
    const [venueDetails, setVenueDetails] = useState(null);
    const [mapsPins, setMapsPins] = useState(null);
    const [venueMaps, setVenueMaps] = useState([]);
    const [showBot, toggleBot] = useState(false);

    const [deviceType] = useState(
        detectMobile() ? "mobile" : "desktop");

    const [showARModal, setShowARModal] = useState(false);
    const [modalData, setModalData] = useState({});

    const mapDetailsSelector = useSelector(
        (state) => state.interactivemap.mapDetails
    );
    const locationSelector = useSelector(
        (state) => state.interactivemap.locationPins
    );

    const venueDetailsSelector = useSelector(
        (state) => state.interactivemap.venueDetails
    );

    const venueMapListSelector = useSelector(
        (state) => state.interactivemap.venueMapList
    );

    const venueMapsDetailsSelector = useSelector(
        (state) => state.interactivemap.venueMapsDetails
    );

    const pathRoute = useSelector((state) => state.interactivemap.pathRoute);

    const editor = useRef(null);
    const floatingbar = useRef({
        desktop: null,
        mobile: null,
    });
    const sidebar = useRef({
        desktop: null,
        mobile: null,
    });
    const actionButtons = useRef(null);

    const mapMeta = useRef({
        mapId: null,
        mapCode: null,
        buildingId: null,
        maps: [],
        mapsData: [],
    });

    useEffect(() => {
        mapMeta.current.mapCode = mapCode;

        isInteractiveMapsInit === true &&
            dispatch(initializeInteractiveMaps());

        editor.current !== null && editor.current.clearAndReset();
        editor.current = null;

        // real-vh css calculations
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--real-vh', `${vh}px`);

        fetchMapDetails();

        // Listen for chatbot toggle event
        const handleToggleChatbot = () => {
            toggleBot(prev => !prev);
        };
        window.addEventListener('toggleChatbot', handleToggleChatbot);

        return () => {
            dispatch(initializeInteractiveMaps());
            editor.current?.clearAndReset();
            editor.current = null;
            isInteractiveMapsInit = false;
            sessionStorage.removeItem("InteractiveMapsInit");
            window.removeEventListener('toggleChatbot', handleToggleChatbot);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initiateEditor = () => {
        const CanvasElement = document.getElementById("interactive-canvas");
        const LabelsElement = document.getElementById("interactiveLabels");
        editor.current = new InteractiveExperience(
            mapDetailsSelector,
            mapCode,
            "ARway-Map",
            CanvasElement,
            {
                loadingComplete: onEditorLoadingComplete,
                onRequestPathBetweenPins: fetchPathBetweenPins,
                onToggleARModal: toggleARModal,
                onUpdateInteractiveSearchAnalytics: updateInteractiveSearchAnalytics,
            },
            LabelsElement
        );

        floatingbar.current.desktop === null &&
            (floatingbar.current.desktop = new FloatingbarDesktop({
                dom: document.getElementById("interactiveStudioFloatingbar"),
            }));

        floatingbar.current.mobile === null &&
            (floatingbar.current.mobile = new FloatingbarMobile({
                dom: document.getElementById("interactiveStudioFloatingbar"),
            }));

        sidebar.current.desktop === null &&
            (sidebar.current.desktop = new SidebarDesktop({
                dom: document.getElementById("interactiveStudioSidebar"),
            }));

        sidebar.current.mobile === null &&
            (sidebar.current.mobile = new SidebarMobile({
                dom: document.getElementById("interactiveStudioSidebar"),
            }));

        actionButtons.current === null &&
            (actionButtons.current = new ActionButtons({
                dom: document.getElementById("interactiveStudioActionButtons"),
            }));
    };

    useEffect(() => {
        if (editor.current) {
            const browser = detectMobile();
            if (browser) {
                floatingbar.current.mobile.updateVisibility(true);
                floatingbar.current.desktop.updateVisibility(false);
            } else {
                floatingbar.current.mobile.updateVisibility(false);
                floatingbar.current.desktop.updateVisibility(true);
            }

            // sidebar toggle
            if (browser) {
                sidebar.current.mobile.updateVisibility(true);
                sidebar.current.desktop.updateVisibility(false);
            } else {
                sidebar.current.mobile.updateVisibility(false);
                sidebar.current.desktop.updateVisibility(true);
            }

            // Listen for pin selection to close chatbot
            const handlePinSelection = (locationPin) => {
                if (locationPin) {
                    toggleBot(false);
                }
            };

            editor.current.on("locationPinSelected", handlePinSelection);

            return () => {
                if (editor.current) {
                    editor.current.off("locationPinSelected", handlePinSelection);
                }
            };
        }
    }, [deviceType, editor.current]);

    const fetchMapDetails = () => {
        dispatch(getMapDetails(mapCode));
    };

    const onEditorLoadingComplete = () => {
        setFetching(false);

        const floatingbarDom = document.getElementById("wsInteractiveFloatingbar_id");

        if (floatingbarDom) {

        }
    }

    const fetchPathBetweenPins = (destinationId, sourcePosition, mapId) => {
        dispatch(
            searchDirectionBetweenPins({
                mapId,
                reqObj: {
                    locationId: destinationId,
                    startPosition: sourcePosition,
                    pathType: "",
                },
            })
        );
    };

    const updateInteractiveSearchAnalytics = (reqObje) => {
        dispatch(updateInteractiveAnalytics(reqObje));
    }

    const toggleARModal = (data) => {
        setModalData(data);
        setShowARModal(true);
    };

    //useEffects
    useEffect(() => {
        if (mapDetailsSelector && !isEqual(mapDetailsSelector, mapDetails)) {

            if (Number(mapCode) !== Number(mapDetailsSelector.metadata.mapCode)) {
                return;
            }

            initiateEditor();
            setMapDetails(mapDetailsSelector);
            mapMeta.current.mapId = mapDetailsSelector.metadata.mapId;
            if (
                mapDetailsSelector.metadata.buildingId &&
                mapDetailsSelector.metadata.buildingId.length > 0
            ) {
                mapMeta.current.buildingId =
                    mapDetailsSelector.metadata.buildingId;
                dispatch(getBuildingDetails(mapDetailsSelector.metadata.buildingId));
            } else {
                mapMeta.current.buildingId = null;
                dispatch(
                    getLocationPins({
                        mapId: mapMeta.current.mapId,
                        type: "map",
                    })
                );
            }
            editor.current !== null &&
                editor.current.initResourceLoading(
                    mapDetailsSelector,
                    mapMeta.current.buildingId
                );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapDetailsSelector]);

    useEffect(() => {
        if (pathRoute) {
            editor.current !== null &&
                editor.current.onPathBetweenPinsResponse(pathRoute);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathRoute]);

    useEffect(
        () => {
            if (
                venueDetailsSelector && mapMeta.current.buildingId &&
                !isEqual(venueDetailsSelector, venueDetails)
            ) {
                setVenueDetails(venueDetailsSelector);
                mapMeta.current.maps = venueMapListSelector;
                editor.current !== null &&
                    editor.current.setVenueDetails(venueDetailsSelector);
                if (venueMapListSelector.length) {
                    dispatch(
                        getVenueMapsDetails(
                            venueMapListSelector.map((map) => map.mapCode)
                        )
                    );
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [venueDetailsSelector]
    );

    useEffect(
        () => {
            if (
                venueMapsDetailsSelector && mapMeta.current.buildingId &&
                venueMapsDetailsSelector.length &&
                !isEqual(venueMapsDetailsSelector, venueMaps)
            ) {
                setVenueMaps(venueMapsDetailsSelector);
                mapMeta.current.mapsData = venueMapsDetailsSelector.map(
                    (map) => ({ metadata: map })
                );
                dispatch(
                    getLocationPins({
                        mapId: mapMeta.current.buildingId,
                        type: "building",
                    })
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [venueMapsDetailsSelector]
    );

    useEffect(() => {
        if (locationSelector && !isEqual(locationSelector, mapsPins)) {
            if (size(mapMeta.current.buildingId)) {
                mapMeta.current.mapsData = mapMeta.current.mapsData.map(
                    (map) => ({
                        ...map,
                        pins:
                            locationSelector?.locationPins.filter(
                                (pin) => pin.mapId === map.metadata.mapId
                            ) || [],
                    })
                );
                editor.current !== null &&
                    editor.current.loadExtraResources(
                        locationSelector.locationPins.filter(
                            (pin) => pin.mapId === mapMeta.current.mapId
                        ) || []
                    );
                editor.current !== null &&
                    editor.current.setLocationPins(
                        locationSelector.locationPins
                    );
                editor.current !== null &&
                    editor.current.loadVenueMaps(mapMeta.current.mapsData);
            } else {
                editor.current !== null &&
                    editor.current.loadExtraResources(
                        locationSelector.locationPins || []
                    );
            }
            setMapsPins(locationSelector);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationSelector]);

    const headerBack = () => {
        toggleBot(false);
    }

    const config = {
        botName: "Arway",
        initialMessages: [createChatBotMessage(`Hello! How can I assist you today?`)],
        customStyles: {
            botMessageBox: { backgroundColor: "#3158da" },
            chatButton: { backgroundColor: "#3158da" },
        },
        customComponents: {
            header: () => <div className="react-chatbot-kit-chat-header" style={{ display: "flex", justifyContent: "space-between" }}><span onClick={headerBack} style={{ fontWeight: "400", color: "#6c707d", fontSize: "10" }}><img style={{ transform: "rotate(90deg)" }} src={BackIcon} alt="<-" /></span> ARway AI Search</div>,
            botAvatar: () => <div className="react-chatbot-kit-chat-bot-avatar-container">A</div>,
        },
        widgets: [
            {
                widgetName: "amenityOptions",
                widgetFunc: (props) => <AmenityOptions {...props} options={props.payload} />,
                props: {},
                mapStateToProps: ["messages", "sessionId"]
            },
        ],
    };

    return (
        <React.Fragment>
            <div
                className="interactiveMaps__loaderContainer"
                style={fetching ? { display: "flex" } : { display: "none" }}
            >
                <div className="interactiveMaps__loaderIconsContainer">
                    <span className="interactiveMaps__loaderLogo"></span>
                    <span className="interactiveMaps__loaderCircle"></span>
                </div>
                <span className="interactiveMaps__loaderText">
                    Opening Interactive Map . . .
                </span>
            </div>
            <div style={fetching ? { display: "none" } : { display: "flex" }}>
                <div
                    className="interactiveMaps"
                    style={fetching ? { display: "none" } : { display: "flex" }}
                >
                    <div className="interactiveMaps__wsSecondRow">
                        <div className="interactiveMaps__wsCanvasContainer">
                            <canvas id="interactive-canvas"></canvas>
                        </div>
                    </div>
                    <div id="interactiveStudioFloatingbar" style={showBot ? { display: "none" } : { display: "flex", overflow: "visible" }}></div>
                    <div id="interactiveStudioSidebar" style={showBot ? { display: "none" } : { display: "flex" }}></div>
                    <div id="interactiveStudioActionButtons"></div>
                    <div id="interactiveLabels"></div>
                </div>
            </div>

            <ARModal
                openModal={showARModal}
                onCloseModal={() => setShowARModal(false)}
                modalData={modalData}
            />

            <div className="interactiveStudio_chatbotDesktop">
                {showBot && (
                    <div style={{}}>
                        <Chatbot
                            config={config}
                            messageParser={MessageParser}
                            actionProvider={ActionProvider}
                            avata
                        /></div>)}
            </div>

        </React.Fragment>
    );
}
